import { twMerge } from "tailwind-merge";
import { Button, Card } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  OrderSummaryContainer,
  OrderSummaryStatusContainerLegacy,
  SpinnerLegacy,
} from "../../components";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { OrderBreadcrumbs } from "../../components/newOrderSummary/OrderBreadcrumbs";
import {
  ORDER_CONFIRMATION,
  ORDER_DETAILS,
  SUPPLIERS,
} from "../../constants/routes";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useSupplierRelationshipList } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useFocusedNavigation } from "../../hooks/useFocusedNavigation";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { renderTaxName } from "../../utils/sellarHelpers";

export const OrderConfirmation = (): ReactElement => {
  const orderId = useParamAsNumber(ORDER_CONFIRMATION, "orderId");

  const query = useOrder(orderId);
  const navigate = useNavigate();

  useFocusedNavigation();

  useEffect(() => {
    if (query.data?.confirmedAt) {
      navigate(ORDER_DETAILS.href({ orderId: query.data.id }), {
        replace: true,
      });
    }
  }, [navigate, query.data?.id, query.data?.confirmedAt]);

  return (
    <>
      <NavigationBar
        title={"Order confirmation"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => {
          navigate(ORDER_DETAILS.href({ orderId }));
        }}
      />
      {query.isLoading ? (
        <SpinnerLegacy />
      ) : (
        query.data && <OrderConfirmationContent order={query.data} />
      )}
    </>
  );
};

const OrderConfirmationContent = ({
  order,
}: {
  order: OrderGetResponseType;
}) => {
  const supplierRelationshipListQuery = useSupplierRelationshipList({
    limit: 1,
    query: [
      {
        type: "buyerId",
        modifier: "EQUALS",
        value: order.RetailerId,
      },
      {
        type: "approved",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const connection = supplierRelationshipListQuery.data?.pages[0]?.[0];

  return (
    <div className="mx-auto mt-4 flex max-w-(--breakpoint-lg) flex-col gap-4 p-4">
      <OrderBreadcrumbs selectedCrumb="Payment" />
      <div className={"grid gap-x-4 gap-y-2 md:grid-cols-[2fr_1fr]"}>
        <OrderSummaryStatusContainerLegacy
          connection={connection}
          createdAt={order.createdAt}
          invoiceCreateStage={order.Supplier.invoiceCreationStage}
          buyerEmails={order.retailerInvoiceEmails ?? []}
          invoiceFinalizedAt={order.invoiceFinalizedAt}
          confirmedAt={order.confirmedAt}
          dispatchedAt={order.dispatchedAt}
          deliveredAt={order.deliveredAt}
          acceptedAt={order.acceptedAt}
          retailerPaidAt={order.retailerPaidAt}
          buyerPostcode={order.Retailer.Business.DeliveryAddress.postalCode}
          dispatchPlannedAt={order.dispatchPlannedAt}
          deliveryPlannedAt={order.deliveryPlannedAt}
          paymentDue={order.paymentDue}
          paymentType={order.paymentType}
          hasNoDraughtDutyRelief={order.hasNoDraughtDutyRelief}
          invoicesEnabled={order.Supplier.invoicesEnabled}
          paidAt={order.paidAt}
          voidedAt={order.voidedAt}
          cancellationDate={
            order.cancelledAt
              ? dayjs(order.cancelledAt).format("DD/MM/YYYY")
              : null
          }
          deliveryDate={dayjs(order.deliveryPlannedAt).format("DD/MM/YYYY")}
          logoUrl={order.Supplier.Business.logoUrl}
          paymentStatus={order.paymentStatus}
          fulfillmentType={order.fulfillmentType}
          orderId={order.id.toString()}
          orderTotal={formatCurrency(order.price, order.currency)}
          stage={"Requested"}
          requestedDaysAgo={dayjs(order.createdAt).fromNow()}
          requestDate={dayjs(order.createdAt).format("DD/MM/YYYY")}
          buyerName={order.Retailer.Business.name}
          supplierName={order.Supplier.Business.name}
          cancelledBy={order.cancelledBy}
          cancelledAt={order.cancelledAt}
          cancellationReason={order.cancellationReason}
          supplierId={order.SupplierId}
          retailerId={order.RetailerId}
          orderItems={order.OrderItems}
          invoiceIssuedAt={order.invoiceIssuedAt}
          customInvoiceNumber={order.customInvoiceNumber}
          paidAmount={order.paidAmount}
          price={order.price}
          supplierUrlId={order.Supplier.urlId}
          deferredPaymentId={order.Payment?.deferredPaymentId ?? null}
          initiator={order.initiator}
          twoOrder={order.TwoOrder}
          SupplierPalletShipment={order.SupplierPalletShipment}
          dueDays={connection?.paymentDue ?? null}
        />
        <div className={"md:col-start-2 md:row-start-1 md:row-end-6"}>
          <OrderSummaryContainer
            items={order.OrderItems.map((orderItem) => ({
              description: orderItem.Product.name ?? "",
              quantity: orderItem.quantity,
              cost: formatCurrency(orderItem.price),
              sku: orderItem.Product.sku,
              promotionAppliedAt: orderItem.promotionAppliedAt,
            }))}
            priceProps={{
              delivery: formatCurrency(order.deliveryCharge, order.currency),
              subTotal: formatCurrency(
                calculateItemTotal(order.OrderItems),
                order.currency,
              ),
              discount:
                order.discountAmount > 0
                  ? {
                      value: formatCurrency(
                        order.discountAmount,
                        order.currency,
                      ),
                      calculatedTotal: formatCurrency(
                        order.discountAmount,
                        order.currency,
                      ),
                    }
                  : undefined,
              tax: {
                name: renderTaxName(order.currency),
                percentage: order.vatPercentage.toString(),
                total: formatCurrency(
                  calculateVatTotal({ ...order, orderItems: order.OrderItems }),
                  order.currency,
                ),
              },
              total: formatCurrency(order.price, order.currency),
            }}
          />
        </div>
        <OrderConfirmationNavigationCard order={order} />
      </div>
    </div>
  );
};

const OrderConfirmationNavigationCard = ({
  order,
}: {
  order: { id: number };
}) => {
  const navigate = useNavigate();

  return (
    <Card className={twMerge("bg-white", "flex flex-col gap-2 p-4")}>
      <div className={"flex flex-col gap-2 md:flex-row"}>
        <Button onClick={() => navigate(SUPPLIERS)}>Back to Suppliers</Button>
        <Button
          color={"secondary"}
          onClick={() => navigate(ORDER_DETAILS.href({ orderId: order.id }))}
        >
          View/Edit Order
        </Button>
      </div>
    </Card>
  );
};
